/**
 * Lens API client — SSE streaming for explanations and quick translations.
 * Ported from EaseVerse Lens api-core.ts, with Chrome dependencies removed.
 * Uses Reader's JWT for auth (no chrome.storage).
 */

const STREAM_READ_TIMEOUT_MS = 30000;
// fetch() connect-phase timeout: abort if no response headers within this window.
// Protects against client-side networks where POST SSE long connections get
// silently dropped by middleboxes/proxies during the LLM TTFB "sentence" period.
// Without this, isLoading stays true forever (bubble spins with no way to recover).
// Value chosen to cover the backend provider fallback chain
// (Gemini 3s → DeepSeek 5s → GPT-4o-mini) plus network jitter.
const CONNECT_TIMEOUT_MS = 12000;
const MAX_SSE_BUFFER_SIZE = 1024 * 1024; // 1 MB

import { API_BASE } from './config.js';

function readWithTimeout(reader, timeoutMs) {
  let timeoutId;
  const result = Promise.race([
    reader.read(),
    new Promise((_, reject) => {
      timeoutId = setTimeout(() => reject(new Error('STREAM_TIMEOUT')), timeoutMs);
    }),
  ]);
  result.then(() => clearTimeout(timeoutId), () => clearTimeout(timeoutId));
  return result;
}

// Map language codes to backend-expected full names
export const LANG_CODE_TO_NAME = {
  'zh': 'Chinese',
  'zh-TW': 'Traditional Chinese',
  'en': 'English',
  'fr': 'French',
  'ja': 'Japanese',
  'ko': 'Korean',
  'de': 'German',
  'es': 'Spanish',
  'pt': 'Portuguese',
  'ru': 'Russian',
  'it': 'Italian',
  'vi': 'Vietnamese',
};

class LensApiClient {
  constructor() {
    this.authToken = null;
  }

  setAuthToken(token) {
    this.authToken = token;
  }

  getTimezoneOffset() {
    return -new Date().getTimezoneOffset();
  }

  buildHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
      'Cache-Control': 'no-cache',
      'X-Timezone-Offset': String(this.getTimezoneOffset()),
      'X-EaseVerse-Client': 'reader',
    };
    if (this.authToken) headers.Authorization = `Bearer ${this.authToken}`;
    return headers;
  }

  /**
   * Stream a full explanation from the LLM.
   */
  async streamExplanation(text, lang, context = '', {
    pageUrl = '',
    requestType = '',
    originalWord = '',
    originalExplanation = '',
    articleContext = null,
    provider = 'auto',
    sourceLang = '',
    bookTitle = '',
    canonicalBookId = '',
    abortSignal = null,
    onChunk = () => {},
    onError = () => {},
    onComplete = () => {},
    onMeta = () => {},
  } = {}) {
    // Bridge external abortSignal + internal connect timeout into one controller.
    // fetch() uses this merged controller; processSSEStream keeps the original
    // abortSignal so connect timer doesn't affect long-lived stream reads.
    const connectController = new AbortController();
    let connectTimedOut = false;
    const connectTimer = setTimeout(() => {
      connectTimedOut = true;
      connectController.abort();
    }, CONNECT_TIMEOUT_MS);
    const onExternalAbort = () => connectController.abort();
    if (abortSignal) {
      if (abortSignal.aborted) connectController.abort();
      else abortSignal.addEventListener('abort', onExternalAbort, { once: true });
    }

    try {
      const headers = this.buildHeaders();

      const body = { text, lang, context, pageUrl, requestType, originalWord, originalExplanation, provider };
      if (articleContext) body.articleContext = articleContext;
      if (sourceLang) body.sourceLang = sourceLang;
      if (bookTitle) body.bookTitle = bookTitle;
      if (canonicalBookId) body.canonicalBookId = canonicalBookId;

      const response = await fetch(`${API_BASE}/answer`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: connectController.signal,
      });

      // Response headers received — connect phase is over.
      // Clear timer and detach external listener so the long-lived stream read
      // only responds to the original abortSignal (and the 30s idle timeout).
      clearTimeout(connectTimer);
      abortSignal?.removeEventListener('abort', onExternalAbort);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        onError(`HTTP ${response.status}: ${errorText}`);
        return;
      }

      // Strip metadata tags ({ab:}, {lang:}, {tts:}) from stream content.
      // Tags may arrive split across SSE chunks or after initial content,
      // so we scan until all expected tags are consumed.
      let metaBuffer = '';
      const META_TAGS = [
        [/\{ab:([^}]+)\}/, 'model'],
        [/\{lang:([^}]+)\}/, 'detectedLang'],
        [/\{tts:(\w+)\}/, 'ttsKey'],
      ];
      const seenTags = new Set();
      const wrappedOnChunk = (content) => {
        if (seenTags.size >= META_TAGS.length) { onChunk(content); return; }
        metaBuffer += content;
        let found = true;
        while (found) {
          found = false;
          for (const [regex, key] of META_TAGS) {
            if (seenTags.has(key)) continue;
            const m = metaBuffer.match(regex);
            if (m) { onMeta({ [key]: m[1] }); metaBuffer = metaBuffer.replace(m[0], ''); seenTags.add(key); found = true; }
          }
        }
        if (/\{[^}]*$/.test(metaBuffer) && metaBuffer.length < 200) return;
        if (metaBuffer) { onChunk(metaBuffer); }
        metaBuffer = '';
      };

      await this.processSSEStream(response, wrappedOnChunk, onError, onComplete, abortSignal);
    } catch (error) {
      clearTimeout(connectTimer);
      abortSignal?.removeEventListener('abort', onExternalAbort);
      // Three-way classification:
      //   1. External abort (user switched words / closed sheet) → silent
      //   2. Connect timeout (our setTimeout fired) → report via onError
      //   3. Other network error → report via onError (original behavior)
      if (abortSignal?.aborted) return;
      if (connectTimedOut) {
        onError(`Connect timeout: no response after ${CONNECT_TIMEOUT_MS / 1000}s`);
        return;
      }
      if (error?.name === 'AbortError') return;
      onError(`Network error: ${error?.message || String(error)}`);
    }
  }

  /**
   * Stream a quick translation or synonym lookup.
   */
  async streamQuickTranslate(text, sentence, context, mode, targetLang, {
    abortSignal = null,
    chunkLevel = null,
    chunkLang = null,
    chunkCategory = null,
    bookTitle = '',
    skipModel = null,
    rejectedHint = null,
    // Thumbs-down → stronger-model regeneration (logged-in only). Backend logs the
    // rejected→replacement sample to translation_downvotes. upgradeTier picks the
    // model: 'sonnet' (default, Lens 👎) or 'opus' (Reader chunk cache_report_bad —
    // tougher classical/literary contexts).
    upgrade = false,
    upgradeTier = 'sonnet',
    downvoteId = null,
    rejectedModel = null,
    rejectedOutput = null,
    bookId = null,
    canonicalBookId = null,
    chapterIndex = null,
    chapterContentHash = null,
    onChunk = () => {},
    onError = () => {},
    onComplete = () => {},
    onMeta = () => {},
    onRetry = null,
  } = {}) {
    // Bridge external abortSignal + internal connect timeout into one controller.
    // fetch() uses this merged controller; processSSEStream keeps the original
    // abortSignal so connect timer doesn't affect long-lived stream reads.
    const connectController = new AbortController();
    let connectTimedOut = false;
    const connectTimer = setTimeout(() => {
      connectTimedOut = true;
      connectController.abort();
    }, CONNECT_TIMEOUT_MS);
    const onExternalAbort = () => connectController.abort();
    if (abortSignal) {
      if (abortSignal.aborted) connectController.abort();
      else abortSignal.addEventListener('abort', onExternalAbort, { once: true });
    }

    try {
      const headers = this.buildHeaders();

      const mappedLang = LANG_CODE_TO_NAME[targetLang];
      if (!mappedLang) {
        console.warn(`[LensApi] Unknown targetLang "${targetLang}", resetting to "zh"`);
        targetLang = 'zh';
      }
      const body = { text, sentence, context, mode, targetLang: LANG_CODE_TO_NAME[targetLang] };
      if (chunkLevel) body.chunkLevel = chunkLevel;
      if (chunkLang) body.chunkLang = chunkLang;
      if (chunkCategory) body.chunkCategory = chunkCategory;
      if (bookTitle) body.bookTitle = bookTitle;
      if (skipModel) body.skipModel = skipModel;
      if (rejectedHint) body.rejectedHint = rejectedHint;
      if (upgrade) {
        body.upgrade = true;
        if (upgradeTier && upgradeTier !== 'sonnet') body.upgradeTier = upgradeTier;
        if (downvoteId) body.downvoteId = downvoteId;
        if (rejectedModel) body.rejectedModel = rejectedModel;
        if (rejectedOutput) body.rejectedOutput = rejectedOutput;
      }
      if (bookId) body.bookId = bookId;
      if (canonicalBookId) body.canonicalBookId = canonicalBookId;
      if (chapterIndex != null) body.chapterIndex = chapterIndex;
      if (chapterContentHash) body.chapterContentHash = chapterContentHash;

      const response = await fetch(`${API_BASE}/quick-translate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal: connectController.signal,
      });

      // Response headers received — connect phase is over.
      // Clear timer and detach external listener so the long-lived stream read
      // only responds to the original abortSignal (and the 30s idle timeout).
      clearTimeout(connectTimer);
      abortSignal?.removeEventListener('abort', onExternalAbort);

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        onError(`HTTP ${response.status}: ${errorText}`);
        return;
      }

      // Strip metadata tags ({tid:}, {ab:}, {lang:}, {tts:}) from stream content.
      // Tags may arrive split across SSE chunks or after initial content,
      // so we scan until all expected tags are consumed.
      let metaBuffer = '';
      const META_TAGS = [
        [/\{ab:([^}]+)\}/, 'model'],
        [/\{tid:([^}]+)\}/, 'translationId'],
        [/\{lang:([^}]+)\}/, 'detectedLang'],
        [/\{tts:(\w+)\}/, 'ttsKey'],
      ];
      const seenTags = new Set();
      const wrappedOnChunk = (content) => {
        if (seenTags.size >= META_TAGS.length) { onChunk(content); return; }
        metaBuffer += content;
        let found = true;
        while (found) {
          found = false;
          for (const [regex, key] of META_TAGS) {
            if (seenTags.has(key)) continue;
            const m = metaBuffer.match(regex);
            if (m) { onMeta({ [key]: m[1] }); metaBuffer = metaBuffer.replace(m[0], ''); seenTags.add(key); found = true; }
          }
        }
        if (/\{[^}]*$/.test(metaBuffer) && metaBuffer.length < 200) return;
        if (metaBuffer) { onChunk(metaBuffer); }
        metaBuffer = '';
      };

      await this.processSSEStream(response, wrappedOnChunk, onError, onComplete, abortSignal, onRetry);
    } catch (error) {
      clearTimeout(connectTimer);
      abortSignal?.removeEventListener('abort', onExternalAbort);
      // Three-way classification:
      //   1. External abort (user switched words / closed sheet) → silent
      //   2. Connect timeout (our setTimeout fired) → report via onError
      //   3. Other network error → report via onError (original behavior)
      if (abortSignal?.aborted) return;
      if (connectTimedOut) {
        onError(`Connect timeout: no response after ${CONNECT_TIMEOUT_MS / 1000}s`);
        return;
      }
      if (error?.name === 'AbortError') return;
      onError(`Network error: ${error?.message || String(error)}`);
    }
  }

  /**
   * Request a regenerated hint (non-streaming).
   * Called when user thumbs-down a pre-generated hint.
   * Returns { hint_zh, hint_en, model } or throws on error.
   */
  async requestRehint({ text, category, level, sentence, context, rejectedHint, targetLang, canonicalBookId, chapterContentHash, sourceLang }) {
    const headers = {
      'Content-Type': 'application/json',
      'X-Timezone-Offset': String(this.getTimezoneOffset()),
    };
    if (this.authToken) headers.Authorization = `Bearer ${this.authToken}`;

    const mappedLang = LANG_CODE_TO_NAME[targetLang] || LANG_CODE_TO_NAME['zh'];

    const response = await fetch(`${API_BASE}/reader/rehint`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        text, category, level, sentence, context,
        rejectedHint,
        targetLang: mappedLang,
        canonicalBookId: canonicalBookId || undefined,
        chapterContentHash: chapterContentHash || undefined,
        sourceLang: sourceLang || undefined,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error');
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    return response.json();
  }

  /**
   * Process SSE stream from response body.
   */
  async processSSEStream(response, onChunk, onError, onComplete, abortSignal, onRetry) {
    if (!response.body) {
      onError('No response body received');
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        if (abortSignal?.aborted) break;

        const { done, value } = await readWithTimeout(reader, STREAM_READ_TIMEOUT_MS);
        if (done) { onComplete(); break; }
        if (abortSignal?.aborted) break;

        buffer += decoder.decode(value, { stream: true });

        if (buffer.length > MAX_SSE_BUFFER_SIZE) {
          onError('Stream buffer overflow');
          return;
        }

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (abortSignal?.aborted) break;
          if (line.startsWith('data: ')) {
            const data = line.slice(6);

            if (data === '[DONE]') {
              onComplete();
              return;
            }

            try {
              const parsed = JSON.parse(data);
              if (parsed.safety_retry) {
                reader.cancel().catch(() => {});
                onRetry?.(parsed.safety_retry);
                return;
              }
              if (parsed.error) {
                onError(`API Error: ${parsed.error}`);
                return;
              }
              const content = parsed.choices?.[0]?.delta?.content
                || parsed.delta?.content
                || parsed.delta
                || parsed.content;
              if (content && typeof content === 'string') {
                onChunk(content);
              }
            } catch {
              if (data.trim()) onChunk(data);
            }
          }
        }
      }
    } catch (streamError) {
      if (streamError?.name === 'AbortError') return;
      if (streamError?.message === 'STREAM_TIMEOUT') {
        reader.cancel().catch(() => {});
        onError('Stream timeout: no data received for 30 seconds');
        return;
      }
      onError(`Stream processing error: ${streamError?.message || String(streamError)}`);
    } finally {
      reader.releaseLock();
    }
  }

  /**
   * Fetch IPA pronunciation for a word (non-streaming).
   * Returns { word, ipa, ipa_us, ipa_uk, found } or null on failure.
   *
   * 两级缓存(2026-08-11 真机:「音标没有做缓存」——同词重复查每次都等
   * 网络):内存 Map + localStorage 持久,单词条目含负缓存(该词库里没
   * 音标就别反复问);多词文本不入缓存(句子查询是 found:false 噪声,
   * 会把 500 条容量挤掉)。网络失败不缓存,下次重试。
   */
  async fetchIPA(word) {
    const key = String(word || '').trim().toLowerCase();
    const cacheable = key && key.length < 40 && !/\s/.test(key);
    const cache = ipaCacheLoad();
    if (cacheable) {
      const hit = cache.get(key);
      if (hit !== undefined) return hit.found ? hit : null;
    }
    try {
      const headers = {
        'Content-Type': 'application/json',
        'X-Timezone-Offset': String(this.getTimezoneOffset()),
      };
      if (this.authToken) headers.Authorization = `Bearer ${this.authToken}`;
      const res = await fetch(`${API_BASE}/ipa`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ word }),
      });
      if (!res.ok) return null;
      const data = await res.json();
      // 带 error 字段的 found:false(配额耗尽 UNAUTH/provider 瞬时故障)是
      // 状态不是事实,禁止入负缓存 —— 否则登录/配额恢复后音标永不再显。
      if (cacheable && !data?.error) {
        const entry = data?.found
          ? { word: data.word, ipa: data.ipa, ipa_us: data.ipa_us, ipa_uk: data.ipa_uk, found: true }
          : { found: false };
        cache.set(key, entry);
        ipaCacheSave();
      }
      return data?.found ? data : null;
    } catch {
      return null;
    }
  }
}

// ── IPA 客户端缓存 ─────────────────────────────────────────────────
// 词→音标是不变映射,值极小({ipa, ipa_us, ipa_uk}),localStorage 足够。
const IPA_LS_KEY = 'ev_ipa_cache_v1';
const IPA_MAX_ENTRIES = 500;
let ipaMem = null;
function ipaCacheLoad() {
  if (ipaMem) return ipaMem;
  ipaMem = new Map();
  try {
    const raw = localStorage.getItem(IPA_LS_KEY);
    if (raw) {
      for (const [k, v] of Object.entries(JSON.parse(raw))) {
        if (v && typeof v.found === 'boolean') ipaMem.set(k, v);
      }
    }
  } catch { /* 隐私模式/损坏条目 → 退化为内存缓存 */ }
  return ipaMem;
}
let ipaSaveTimer = null;
function ipaCacheSave() {
  if (ipaSaveTimer) clearTimeout(ipaSaveTimer);
  ipaSaveTimer = setTimeout(() => {
    try {
      const entries = [...ipaMem.entries()].slice(-IPA_MAX_ENTRIES);
      localStorage.setItem(IPA_LS_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch { /* 配额满则放弃,退化为内存态 */ }
  }, 500);
}
/** 测试用 */
export function clearIPACache() { ipaMem = null; try { localStorage.removeItem(IPA_LS_KEY); } catch { /* noop */ } }

export const lensApi = new LensApiClient();
export default lensApi;
